<script lang="ts">
import { defineComponent } from 'vue';
import algoliasearch from 'algoliasearch/lite';

import {
  AisHits,
  AisInstantSearch,
  AisSearchBox,
  AisStateResults,
  AisStats,
} from 'vue-instantsearch/vue3/es';

export default defineComponent({
  components: {
    AisHits,
    AisInstantSearch,
    AisSearchBox,
    AisStateResults,
    AisStats,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    indexName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      algoliaClient: null as any,
      searchClient: null as any,
      selectedFaq: null as { question: string; answer: string } | null,
      isModalOpen: false,
    };
  },
  created() {
    this.algoliaClient = algoliasearch(this.appId, this.searchApiKey);
    this.searchClient = {
      ...this.algoliaClient,
      search: (requests: any) => {
        // Skip search request if all requests have empty query parameters, still need to return a promise because Algolia expects one
        if (requests.every(({ params }: { params: any }) => !params.query)) {
          return Promise.resolve({
            results: requests.map(() => ({
              hits: [],
              nbHits: 0,
              page: 0,
              nbPages: 0,
              hitsPerPage: 20,
              processingTimeMS: 0,
              query: '',
              params: '',
            })),
          });
        }
        // Make the search request to Algolia
        return this.algoliaClient.search(requests);
      },
    };
  },
  mounted() {
    // Listen for escape key to close modal
    document.addEventListener('keydown', this.handleEscape);
    // Listen for clicks outside the search component
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleEscape);
    document.removeEventListener('click', this.handleClickOutside);
    // Restore body scroll if modal was open
    if (this.isModalOpen) {
      document.body.style.overflow = '';
    }
  },
  methods: {
    focusSearchInput() {
      const { searchBox } = this.$refs as {
        searchBox: { $refs: { searchInput: { $refs: { input: HTMLInputElement } } } };
      };
      const { searchInput } = searchBox.$refs;
      const { input } = searchInput.$refs;
      // Focus the search input (using autofocus is only reliable on page load)
      input.focus();
    },
    openModal(faq: { question: string; answer: string }) {
      this.selectedFaq = faq;
      this.isModalOpen = true;
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    },
    closeModal() {
      this.isModalOpen = false;
      this.selectedFaq = null;
      // Restore body scroll
      document.body.style.overflow = '';
    },
    handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape' && this.isModalOpen) {
        this.closeModal();
      }
    },
    handleClickOutside(event: MouseEvent) {
      const searchContainer = this.$el;
      // Check if the click was outside the search component
      if (searchContainer && !searchContainer.contains(event.target as Node)) {
        // Only clear if there's actually a search query
        const { instantSearch, searchBox } = this.$refs as {
          instantSearch: any;
          searchBox: any;
        };
        if (instantSearch && instantSearch.instantSearchInstance) {
          const currentQuery =
            instantSearch.instantSearchInstance.getUiState()?.[this.indexName]?.query;
          if (currentQuery && currentQuery.length > 0) {
            this.clearSearch();
          }
        }
      }
    },
    clearSearch() {
      const { instantSearch, searchBox } = this.$refs as {
        instantSearch: any;
        searchBox: any;
      };

      if (instantSearch && instantSearch.instantSearchInstance) {
        instantSearch.instantSearchInstance.setUiState({
          [this.indexName]: {
            query: '',
          },
        });
      }
      // Also clear the input field value directly and trigger events
      if (searchBox && searchBox.$refs && searchBox.$refs.searchInput) {
        const { searchInput } = searchBox.$refs;
        if (searchInput.$refs && searchInput.$refs.input) {
          const { input } = searchInput.$refs;
          input.value = '';
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    },
  },
});
</script>
<template>
  <div id="faq-search" class="text-gtg-claret relative mx-auto h-13 w-full md:w-86">
    <div class="relative">
      <div class="absolute left-0 right-0 top-0">
        <div
          class="shadow-default rounded-lg bg-white focus-within:ring-2 focus-within:ring-gtg-pink-500 focus-within:ring-offset-4 focus-within:ring-offset-gtg-teal-500"
        >
          <ais-instant-search
            ref="instantSearch"
            :search-client="searchClient"
            :index-name="indexName"
          >
            <ais-search-box
              ref="searchBox"
              show-loading-indicator
              placeholder="Ask us a question"
              :class-names="{
                'ais-SearchBox-form': 'relative',
                'ais-SearchBox-input':
                  'bg-transparent py-3 px-4 border-0 focus:outline-none focus:ring-0 w-full placeholder:text-gtg-claret [&::-webkit-search-cancel-button]:hidden',
                'ais-SearchBox-reset': '-translate-y-1/2 absolute right-11 top-1/2',
                'ais-SearchBox-submit':
                  '-translate-y-1/2 absolute right-4 top-1/2 focus-visible:text-gtg-teal-500',
                'ais-SearchBox-loadingIndicator': '-translate-y-1/2 absolute right-4 top-1/2',
              }"
            >
              <template #submit-icon>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  @click.prevent="focusSearchInput"
                >
                  <path
                    d="M17 17L12.3333 12.3333M13.8889 8.44444C13.8889 11.4513 11.4513 13.8889 8.44444 13.8889C5.43756 13.8889 3 11.4513 3 8.44444C3 5.43756 5.43756 3 8.44444 3C11.4513 3 13.8889 5.43756 13.8889 8.44444Z"
                    stroke="#161818"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </template>
            </ais-search-box>
            <ais-stats>
              <template #default="{ nbHits, query }">
                <section v-if="nbHits > 0 && query" class="relative z-10 bg-white px-4 pb-5 pt-3.5">
                  <div
                    class="max-h-[21rem] overflow-scroll pr-3 [scrollbar-color:theme(colors.gtg.pink.500)_theme(colors.gtg.grey.200)] [scrollbar-width:thin]"
                  >
                    <ais-state-results>
                      <ais-hits>
                        <template #default="{ items }">
                          <ul class="flex flex-col justify-start divide-y border-t">
                            <li v-for="item in items" :key="item.objectID">
                              <button
                                type="button"
                                @click="openModal({ question: item.question, answer: item.answer })"
                                class="block w-full py-2 text-left text-base hover:underline"
                              >
                                {{ item.question }}
                              </button>
                            </li>
                          </ul>
                        </template>
                      </ais-hits>
                    </ais-state-results>
                  </div>
                </section>
                <section v-else-if="nbHits === 0 && query.length > 0" class="px-4 pb-5 pt-3.5">
                  <p class="border-t py-2 text-sm">No results have been found for {{ query }}.</p>
                </section>
                <!-- Blank for the else as we want no output -->
                <div v-else></div>
              </template>
            </ais-stats>
          </ais-instant-search>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isModalOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
        @click.self="closeModal"
      >
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition-all duration-300 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="isModalOpen"
            class="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
          >
            <!-- Modal Header -->
            <div class="flex items-start px-10 pt-8">
              <h2 class="text-2xl font-bold text-gtg-pink-700">{{ selectedFaq?.question }}</h2>
              <button
                type="button"
                @click="closeModal"
                class="hover:text-gtg-claret absolute right-4 top-4 rounded text-gtg-grey-600 focus:outline-none focus:ring-2 focus:ring-gtg-pink-500"
                aria-label="Close modal"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>

            <!-- Modal Body -->
            <div class="flex-1 overflow-y-auto px-10 py-8">
              <div class="rich-text rich-text--modal" v-html="selectedFaq?.answer"></div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>
